import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faServer, faCopy, faCheck, faKey, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';
import './RepairCodePanel.css';

const formatExpiry = (iso) =>
  new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });

// "Réparation serveur": a Lonnii Desktop till that cannot find its shop's server cannot be pointed at one by hand.
// The shop phones us with the till's id (shown on its repair screen) and the server's address; we issue the code
// here, and the till accepts that one address, for that one till, and nothing else.
const RepairCodePanel = () => {
  const [device, setDevice] = useState('');
  const [address, setAddress] = useState('');
  const [hours, setHours] = useState('24');
  const [allowRemote, setAllowRemote] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setResult(null);
    setCopied(false);

    try {
      const response = await api.post('/dashboard-admin/repair-code', {
        device,
        address,
        hours: Number(hours),
        allowRemote
      });
      setResult(response.data);
    } catch (err) {
      const status = err.response?.status;
      setError(
        err.response?.data?.error ||
          (status === 404
            ? 'Le serveur ne connaît pas encore cette fonction (404). Le backend doit être déployé et rechargé.'
            : status
              ? `Erreur du serveur (${status})`
              : 'Serveur injoignable')
      );
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // The code stays selectable on screen if the clipboard is refused.
    }
  };

  const remoteAddress = Boolean(error) && /réseau du magasin/.test(error);

  return (
    <div className="settings-section repair-panel">
      <h3>
        <FontAwesomeIcon icon={faServer} /> Réparation serveur
      </h3>
      <p className="repair-intro">
        Un poste Lonnii Desktop qui ne trouve pas le serveur de son magasin ne peut pas être redirigé à la main.
        Demandez au client l'<strong>identifiant du poste</strong> (affiché sur son écran « Réparer la connexion »)
        et l'<strong>adresse du serveur</strong>, puis générez le code. Il ne vaut que pour ce poste et cette adresse.
      </p>

      <form onSubmit={generate} className="repair-form">
        <div className="repair-field">
          <label htmlFor="repair-device">Identifiant du poste</label>
          <input
            id="repair-device"
            type="text"
            placeholder="3194-39E8-5A96-7ECF"
            value={device}
            onChange={(e) => setDevice(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            required
          />
        </div>

        <div className="repair-field">
          <label htmlFor="repair-address">Adresse du serveur</label>
          <input
            id="repair-address"
            type="text"
            placeholder="192.168.1.20:5280"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            required
          />
          <small className="repair-hint">
            Avec le port du serveur du magasin (5280 par défaut). Le code ne vaut que pour cette adresse exacte.
          </small>
        </div>

        <div className="repair-field repair-field-short">
          <label htmlFor="repair-hours">Valable (heures)</label>
          <input
            id="repair-hours"
            type="number"
            min="1"
            max="336"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            required
          />
        </div>

        <label className="repair-remote">
          <input type="checkbox" checked={allowRemote} onChange={(e) => setAllowRemote(e.target.checked)} />
          <span>Autoriser une adresse à distance (hors du réseau du magasin)</span>
        </label>
        {allowRemote && (
          <p className="repair-warning">
            <FontAwesomeIcon icon={faExclamationTriangle} /> Un serveur à distance relève de l'offre en ligne.
            N'autorisez ceci que si le magasin y a souscrit.
          </p>
        )}

        {error && (
          <div className={`repair-error ${remoteAddress ? 'repair-error-soft' : ''}`}>{error}</div>
        )}

        <button type="submit" className="btn-primary repair-submit" disabled={busy}>
          <FontAwesomeIcon icon={faKey} /> {busy ? 'Génération…' : 'Générer le code'}
        </button>
      </form>

      {result && (
        <div className="repair-result">
          <div className="repair-result-head">
            <strong>Code de réparation</strong>
            <span>
              Poste {result.device} · {result.address} · valable jusqu'au {formatExpiry(result.expiresAt)}
            </span>
          </div>
          <code className="repair-code">{result.code}</code>
          <button type="button" className="btn-secondary" onClick={copy}>
            <FontAwesomeIcon icon={copied ? faCheck : faCopy} /> {copied ? 'Copié' : 'Copier'}
          </button>
          <p className="repair-note">
            Transmettez-le au client par téléphone ou message. Il est consigné dans le journal d'audit (sans le code
            lui-même).
          </p>
        </div>
      )}
    </div>
  );
};

export default RepairCodePanel;
